import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../lib/utils';

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement> & {
    containerClassName?: string;
  }
>(({ className, containerClassName, ...props }, ref) => (
  <div
    data-slot="table-container"
    className={cn('relative w-full overflow-auto', containerClassName)}
  >
    <table
      ref={ref}
      data-slot="table"
      className={cn('group/table w-full caption-bottom text-sm font-normal', className)}
      {...props}
    />
  </div>
));
Table.displayName = 'Table';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    data-slot="table-header"
    className={cn('[&_tr]:border-b', className)}
    {...props}
  />
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    data-slot="table-body"
    className={cn('[&_tr:last-child]:border-0', className)}
    {...props}
  />
));
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    data-slot="table-footer"
    className={cn('border-t font-medium', className)}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      data-slot="table-row"
      className={cn(
        'group border-b data-[state=selected]:bg-surface hover:bg-surface-raised',
        className,
      )}
      {...props}
    />
  ),
);
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    data-slot="table-head"
    className={cn(
      'heading-meta h-10 px-4 text-left align-middle whitespace-nowrap text-tertiary [&:has([role=checkbox])]:pr-0',
      'transition-colors',
      className,
    )}
    {...props}
  />
));
TableHead.displayName = 'TableHead';

interface TableHeadSortProps<TColumn extends string = string> {
  column: TColumn;
  currentSort: string;
  onSortChange: (column: TColumn) => void;
  children: React.ReactNode;
  className?: string;
}

function TableHeadSort<TColumn extends string = string>({
  column,
  currentSort,
  onSortChange,
  children,
  className,
}: TableHeadSortProps<TColumn>) {
  const [currentCol, currentOrder] = currentSort.split(':');
  const isActive = currentCol === column;
  const isAsc = isActive && currentOrder === 'asc';
  const isDesc = isActive && currentOrder === 'desc';

  function renderSortIcon() {
    const baseIconClass = 'absolute inset-0 size-3';
    return (
      <>
        <ArrowUp
          className={cn(
            baseIconClass,
            'transition-transform',
            isAsc ? 'translate-y-0' : 'translate-y-full',
          )}
        />
        <ArrowDown
          className={cn(
            baseIconClass,
            'transition-transform',
            isDesc ? 'translate-y-0' : '-translate-y-full',
          )}
        />
        <ChevronsUpDown
          className={cn(
            baseIconClass,
            'opacity-80 transition-opacity md:opacity-40',
            isActive ? 'opacity-0!' : 'group-hover/table-head-sort:opacity-80',
          )}
        />
      </>
    );
  }

  return (
    <button
      type="button"
      tabIndex={0}
      className={cn(
        'group/table-head-sort heading-meta flex w-full cursor-pointer select-none items-center gap-1 border-none bg-transparent! p-0 text-left whitespace-nowrap',
        className,
      )}
      onClick={() => onSortChange(column)}
    >
      {children}
      <div className="relative size-3 overflow-hidden">{renderSortIcon()}</div>
    </button>
  );
}
TableHeadSort.displayName = 'TableHeadSort';

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    data-slot="table-cell"
    className={cn('p-4 align-middle transition-colors [&:has([role=checkbox])]:pr-0', className)}
    {...props}
  />
));
TableCell.displayName = 'TableCell';

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    data-slot="table-caption"
    className={cn('border-t p-4 text-sm text-quaternary', className)}
    {...props}
  />
));
TableCaption.displayName = 'TableCaption';

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableHeadSort,
  TableRow,
};
